// M17 — hücre "Geçmiş" sekmesindeki sürüm listesinin saf görünüm mantığı (KpiCellHistory ince
// kalır). Satır iki satırlıdır (460 px popover'a sığar): "v3 · onaylandı (güncel)" /
// "danışman · 08 Eki 14:31"; değer bir önceki sürüme göre değiştiyse "12 → 15 MWh", neden varsa
// "Neden: …". Kopya tek kaynak `KPI_COPY.cellHistory` (e2e spec 15 `COPY.history` eşlenik kopya).

import type { KpiValueVersion } from "@mahir/shared";
import { KPI_EVIDENCE_COPY } from "./kpi-evidence-copy";
import { KPI_COPY, formatKpiCell } from "./kpi-ui";

export const VERSION_LIST_INITIAL = 5;

export interface VersionLineView {
  id: string;
  head: string;
  meta: string;
  title: string;
  change: string | null;
  /** DR14: birim değişimi olay satırı (`unit_change` sürümü) — sayılar değişmese de görünür. */
  event: string | null;
  reason: string | null;
}

/** "08 Eki 2026 14:31" (yerel saat; DR14: yıl dahil). */
export function formatVersionDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  return `${day} ${time}`;
}

function valueText(v: KpiValueVersion, percent: boolean): string {
  return formatKpiCell(
    {
      status: v.status,
      isDerived: false,
      ...(v.numericValue !== null ? { value: v.numericValue } : {}),
      ...(v.textValue !== null ? { text: v.textValue } : {}),
    },
    percent ? { percent: true } : {},
  ).text;
}

/** Sürümün birimi: DTO alanı varsa o (null = "bilgi yok"); alan hiç yoksa (eski DTO) çağıranın birimi. */
function versionUnit(v: KpiValueVersion, fallback: string | null): string | null {
  return v.unit !== undefined ? v.unit : fallback;
}

/** `versions` en yeni önce (sunucu sırası). */
export function versionLines(
  versions: readonly KpiValueVersion[],
  opts: { unit: string | null; percent: boolean; currentUserId: string | null },
): VersionLineView[] {
  const C = KPI_COPY.cellHistory;
  const H = KPI_EVIDENCE_COPY.history;
  // birim yalnız sayısal değerin ardından (XXX / Ölçülmüyor metnine eklenmez); null = işaret
  const unitSuffix = (v: KpiValueVersion): string => {
    if (opts.percent || v.status !== "filled" || v.numericValue === null) return "";
    const u = versionUnit(v, opts.unit);
    if (u) return ` ${u}`;
    return v.unit === null ? ` (${C.noUnit})` : "";
  };
  return versions.map((v, i) => {
    const prev = versions[i + 1];
    const cur = valueText(v, opts.percent);
    const unit = unitSuffix(v);
    const before = prev ? valueText(prev, opts.percent) : null;
    // iki birim: önceki sürümün birimi de yazılır ("1.284.000 TL → 1.284 Bin TL")
    const unitsDiffer =
      prev !== undefined && versionUnit(prev, opts.unit) !== versionUnit(v, opts.unit);
    const beforeFull =
      before === null ? null : unitsDiffer ? `${before}${unitSuffix(prev)}` : before;
    const change =
      beforeFull === null
        ? v.status === "empty"
          ? null
          : `${cur}${unit}`
        : beforeFull !== `${cur}` || unitsDiffer
          ? `${beforeFull} → ${cur}${unit}`
          : null;
    const who =
      v.changedBy && opts.currentUserId && v.changedBy === opts.currentUserId
        ? H.you
        : v.changedByKind === "client"
          ? H.client
          : H.internal;
    const ev = v.unitEvent;
    return {
      id: v.id,
      head: `v${v.version.toString()} · ${C.events[v.changeKind]}${i === 0 ? ` ${C.current}` : ""}`,
      meta: `${who} · ${formatVersionDate(v.changedAt)}`,
      title: v.changedByName ?? who,
      change,
      event: ev ? C.unitEvent(ev.from, ev.to, ev.count, ev.factor, ev.mode) : null,
      reason: v.changeReason ? `${C.reason} ${v.changeReason}` : null,
    };
  });
}
